<template>
  <div>
    <button class="create-btn"
            @click="setShowModal"
            :content="'<p class=' + 'tooltip-shortcut' + '>' + $t('vue2.common.press') + '<span>N</span></p>'"
            v-tippy="{
                    arrow: true,
                    placement : 'bottom'
                  }">
      {{ $t('vue2.substitution.create_absence') }}
    </button>
    <transition name="modal">
      <AppModal v-if="showModal"
                :isShowFooter="false"
                @close="closeModal">
        <h1 slot="header">{{ $t('vue2.substitution.create_absence') }}</h1>
        <div slot="body" class="create-absence__form">
          <div class="create__form-group">
            <label>{{ $t('vue2.substitution.who') }}</label>
            <SubsCreateDSelect
              id="create_absence_user"
              :items="users"
              @update="setUsers">
            </SubsCreateDSelect>
            <span class="error-message"
                  v-if="!isUserSelected && showErrorMessages">{{ $t('vue2.substitution.person_must_be_filled') }}</span>
          </div>
          <div class="create__form-group">
            <label>{{ $t('vue2.substitution.reason') }}</label>
            <div class="absence-reason">
              <dSelect :items="subsAbsenceReasonsSorted"
                       :receiveData="reasonCreated"
                       :multiple="false"
                       :placeholder="$t('vue2.substitution.choose_absence_reason')"
                       @update="setReason"
                       @searchText="setReasonSearchText"
                       @onSearchTextDebounced="onSearchTextDebounced"
                       @enter="isCreateAbsenceReason = true"
                       @close="onCloseDSelectAbsenceReasons">
                <div slot="noMatch">
                  <h4 class="create-absence-link" v-if="userHasPermission('can_manage_absence_reasons')"
                      @click="isCreateAbsenceReason = true">
                    + {{ $t('vue2.substitution.create') }}: "{{ reasonSearchText }}"
                  </h4>
                  <h4 v-else>
                    {{ $t('vue2.dselect.no_matching_results') }}
                  </h4>
                </div>
              </dSelect>
              <div class="icon__bg-circle absence-reasons__settings"
                   v-if="userHasPermission('can_manage_absence_reasons') && isShowCreateAbsenceReasonButton"
                   v-tippy="{
                arrow: true,
                animateFill: false,
                animation : 'shift-away',
                zIndex: '999999992',
                boundary: 'window'
              }"
                   :content='$t("vue2.substitution.create") + ` "` + reasonSearchText + `" ` + i18n[locale]["as_a_new_reason"]'
                   @click="isCreateAbsenceReason = true">
                <span class="d-icon-plus"></span>
              </div>
            </div>
            <span class="error-message"
                  v-if="!isReasonSelected && showErrorMessages">{{
                $t('vue2.substitution.reason_must_be_filled')
              }}</span>
          </div>
          <div class="create__form-group">
            <label>{{ $t('vue2.substitution.date') }}</label>
            <div class="flex">
              <DatepickerRange
                :allowSameDate="true"
                :startdate="getDate"
                :enddate="endDate"
                @startchanged="setStartDate"
                @endchanged="setEndDate">
              </DatepickerRange>
              <div class="absence-no-end-date" v-if="isOngoingAbsenceAllowed"
                   :class="{'absence-no-end-date--active': isAbsenceOngoing}">
                <div class="absence-no-end-date__inner" @click.stop.prevent="setIsAbsenceOngoing(!isAbsenceOngoing)">
                  <app-toggle :prop-on="isAbsenceOngoing"></app-toggle>
                  <label>{{ $t('vue2.subs_ongoing_absence.ongoing_absence') }}</label>
                </div>
                <DQuestionMark>
                  <p slot="answer" class="text-dark">{{ $t('vue2.subs_ongoing_absence.ongoing_absence_explained') }}</p>
                </DQuestionMark>
              </div>
              <div class="absence-no-end-date" v-else>
                <p class="text-explainer">{{ $t('vue2.subs_ongoing_absence.cant_be_ongoing_absence') }}</p>
              </div>
            </div>
            <div class="do-warning marg-1-top" v-if="absentUsersWithOngoingAbsenceAlready.length > 0">
              <b class="marg-05-right" v-for="user in absentUsersWithOngoingAbsenceAlready" :key="user">{{ user }} </b> {{ i18n[locale]['already_has_ongoing_absence'] }}
            </div>
          </div>
          <div class="create__form-group">
            <label>{{ $t('vue2.substitution.time') }}</label>
            <div class="time-changer__group">
              <TimeChanger
                :showIcon="true"
                :start="startTime"
                :end="endTime"
                :isFiveMinutesInterval="false"
                @updateStart="setStartTime"
                @updateEnd="setEndTime">
              </TimeChanger>
              <div class="hint--right" :aria-label="i18n[locale]['set_default_start_and_endtime']">
                <div class="icon__bg-circle time-changer__settings" @click="isShowSettings = true">
                  <span class="icon-cog2"></span>
                </div>
              </div>
            </div>
          </div>
          <div class="create__form-group create__form-group__note-to-absence" v-if="isStartDateSameDayAsEndDate">
            <label>{{ i18n[locale]['note_to_absence'] }}</label>
            <textarea class="d-input" v-model="noteForAbsentReason"></textarea>
          </div>
          <div class="modal-footer">
            <button class="bttn create-btn right marg-10"
                    @click="submitUser"
                    :content="saveKeyTooltip"
                    v-tippy="{
                      placement : 'top',
                      zIndex: 12312312312312
                    }">
              {{ $t('vue2.substitution.create_absence') }}
            </button>
          </div>
        </div>
      </AppModal>
    </transition>
    <DModal v-if="isShowSettings" @close="isShowSettings = false">
      <div class="d-modal" slot="insertComponent" style="width: 400px">
        <div class="d-modal__header">
          <h2>{{ i18n[locale]['standard_setting'] }}</h2>
          <div class="d-modal__close icon__bg-circle" @click="isShowSettings = false">
            <span class="icon-cross2"></span>
          </div>
        </div>
        <div class="d-modal__content">
          <label class="d-input marg-1-bottom">{{ i18n[locale]['set_default_start_and_endtime'] }}</label>
          <TimeChanger
            class="marg-1-top"
            :showIcon="true"
            :start="standardStartTime"
            :end="standardEndTime"
            :isFiveMinutesInterval="false"
            @updateStart="setStandardStartTime"
            @updateEnd="setStandardEndTime">
          </TimeChanger>
        </div>
        <div class="d-modal__buttons pad-1">
          <button class="bttn bttn-simple marg-1-right" @click="isShowSettings = false">{{
              $t('vue2.common.cancel')
            }}
          </button>
          <button class="bttn submit-button"
                  @click="submitStandardSettings">
            {{ $t('vue2.common.save') }}
          </button>
        </div>
      </div>
    </DModal>
    <AbsenceReasonsCreate
      v-if="isCreateAbsenceReason"
      :initTitle="reasonSearchText"
      :reasons="subsAbsenceReasonsSorted"
      @create="createAbsenceReason"
      @cancel="isCreateAbsenceReason = false"
    />
  </div>
</template>

<script>
import AppModal from '../common/AppModal'
import DModal from '../common/DModal'
import dSelect from '../DSelect/DSelect'
import DatepickerRange from '../datepicker/DatePickerRange'
import TimeChanger from '../TimeChanger'
import DaySelector from './SubsCreateDayPicker'
import moment from 'moment'
import docendo from '../../docendo'
import {mapGetters} from 'vuex'
import AppToggle from '../common/AppToggle'
import DQuestionMark from '../common/DQuestionMark'
import SubsCreateDSelect from './SubsCreateDSelect'
import AbsenceReasonsCreate from '../AbsenceReasons/AbsenceReasonsCreate.vue'

export default {
  components: {
    AbsenceReasonsCreate,
    SubsCreateDSelect,
    AppToggle, AppModal, DModal, dSelect, DatepickerRange, TimeChanger, DaySelector, DQuestionMark
  },
  props: {
    users: {
      type: Array,
      default: () => []
    },
    usersWithOngoingAbsenceInHash: {
      type: Object,
      default: () => {}
    },
    allUsersInCalIdHash: {
      type: Object,
      default: () => {}
    },
    schoolId: {
      default: null
    }
  },
  computed: {
    ...mapGetters([
      'subsAbsenceReasonsSorted',
      'locale'
    ]),
    absentUsersWithOngoingAbsenceAlready () {
      if (!this.isAbsenceOngoing) {
        return []  // Early return if absence isn't ongoing
      }

      // Filter users with ongoing absence and map their names (or IDs if name is unavailable)
      return this.usersAbsent
        .filter(absentUserCalId => this.usersWithOngoingAbsenceInHash[absentUserCalId]) // Only keep users with ongoing absence
        .map(absentUserCalId => {
          const user = this.allUsersInCalIdHash[absentUserCalId]
          return user ? user.name : absentUserCalId // Return user name or ID if name isn't found
        })
    },
    text () {
      return ''
    },
    user () {
      return this.$store.state.user
    },
    isUserLoaded () {
      return this.$store.state.isUserLoaded
    },
    isPermissionsActivatedForSchool () {
      return this.$store.getters.isPermissionsActivatedForSchool
    },
    userPermissions () {
      return this.$store.getters.userPermissions
    },
    dbEventStart () {
      let date = moment(this.startDate).format('YYYY-MM-DD')
      let time = this.startTime.locale('LT')
      return moment(date).set({
        hour: time.get('hour'),
        minutes: time.get('minutes')
      })
    },
    dbEventEnd () {
      let date = moment(this.endDate).format('YYYY-MM-DD')
      let time = this.endTime.locale('LT')
      return moment(date).set({
        hour: time.get('hour'),
        minutes: time.get('minutes')
      })
    },
    isStartDateSameDayAsEndDate () {
      return moment(this.startDate).format('YYYY-MM-DD') === moment(this.endDate).format('YYYY-MM-DD')
    },
    isUserSelected () {
      return this.usersAbsent.length > 0
    },
    isReasonSelected () {
      return this.reason !== ''
    },
    getDate () {
      return this.$store.state.subs.date
    },
    isOngoingAbsenceAllowed () {
      let endDate = moment(this.endDate).format('YYYY-MM-DD')
      let now = moment().format('YYYY-MM-DD')
      return moment(endDate).isSameOrAfter(now)
    },
    userSettings () {
      return this.$store.state.userSettings
    },
    metaKey () {
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0
      return isMac ? '⌘' : 'CTRL'
    },
    saveKeyTooltip () {
      return '<p class="tooltip-shortcut tooltip-shortcut--double-key"><span>' + this.metaKey + '</span>' + '<span>Enter</span></p>'
    }
  },
  data () {
    return {
      isCreateAbsenceReason: false,
      i18n: {
        da: {
          note_to_absence: 'Eventuel note til fraværet',
          set_default_start_and_endtime: 'Sæt standard start- og sluttid',
          standard_setting: 'Standard indstilling',
          as_a_new_reason: 'som ny årsag',
          already_has_ongoing_absence: 'har allerede løbende fravær'
        },
        en: {
          note_to_absence: 'Note to absence',
          set_default_start_and_endtime: 'Set default start and end time',
          standard_setting: 'Standard setting',
          as_a_new_reason: 'as a new absence',
          already_has_ongoing_absence: 'already has ongoing absence'
        },
        nb: {
          note_to_absence: 'Merknad til fravær',
          set_default_start_and_endtime: 'Angi standard start- og sluttid',
          standard_setting: 'Standardinnstilling',
          as_a_new_reason: 'som en ny årsak',
          already_has_ongoing_absence: 'har allerede pågående fravær'
        }
      },
      noteForAbsentReason: '',
      showModal: false,
      showErrorMessages: false,
      usersAbsent: [],
      startDate: moment(),
      endDate: moment(),
      reason: '',
      reasonCreated: '',
      startTime: moment().set({'hour': 8, 'minutes': 0}),
      endTime: moment().set({'hour': 16, 'minutes': 0}),
      standardStartTime: moment().set({'hour': 8, 'minutes': 0}),
      standardEndTime: moment().set({'hour': 16, 'minutes': 0}),
      isAbsenceOngoing: false,
      isShowSettings: false,
      reasonSearchText: '',
      isShowCreateAbsenceReasonButton: false,
      isEventListenerForSubmitAdded: false,
      isEndDateManipulatedFromDev: false,
      originalEndDate: null
    }
  },
  methods: {
    setIsAbsenceOngoing (bool) {
      this.isAbsenceOngoing = bool
      if (bool) {
        const start = moment(this.startDate).format('YYYY-MM-DD')
        const end = moment(this.endDate).format('YYYY-MM-DD')
        this.originalEndDate = end
        // Gem om den oprindelige slutdato er en fredag
        const endWasFriday = moment(end).isoWeekday() === 5
        let newEnd = end
        // Hvis start og slut er samme dag, så læg 1 dag til slutdato
        if (start === end) {
          newEnd = moment(end).add(1, 'day')
          this.isEndDateManipulatedFromDev = true
        }
        // Hvis slutdatoen (oprindeligt valgt) var fredag, så sørg for at mandag også er med
        if (endWasFriday) {
          newEnd = moment(end).add(3, 'days') // fredag -> mandag
          this.isEndDateManipulatedFromDev = true
        }
        this.endDate = newEnd
      } else {
        // Slås den fra, gør vi ikke andet end at sikre flaget er falsk
        // (redigerings-tilfældet er dækket af linjen øverst)
        if (this.isEndDateManipulatedFromDev) {
          this.endDate = moment(this.originalEndDate).format('YYYY-MM-DD')
          this.isEndDateManipulatedFromDev = false
        }
      }
    },
    onCloseDSelectAbsenceReasons () {
      this.isShowCreateAbsenceReasonButton = false
    },
    setIsShowCreateAbsenceReasonButton () {
      const idx = this.subsAbsenceReasonsSorted.findIndex(absenceReason => absenceReason.name === this.reasonSearchText)
      this.isShowCreateAbsenceReasonButton = this.reasonSearchText.length > 0 && idx < 0
    },
    onSearchTextDebounced () {
      this.setIsShowCreateAbsenceReasonButton()
    },
    setIsAbsenceRunning (val) {
      if (this.isAbsenceOngoing !== val) {
        this.isAbsenceOngoing = !this.isAbsenceOngoing
      }
    },
    userHasPermission (val) {
      if (!this.user) {
        return false
      }
      if (this.isPermissionsActivatedForSchool) {
        return this.userPermissions.indexOf(val) > -1 || this.user.admin
      } else {
        return true
      }
    },
    setShowModal () {
      this.showModal = true
      this.addEventListenerSubmit()
      if (this.userSettings) {
        if (this.userSettings.subSettingsCreateTime) {
          let start = moment(this.userSettings.subSettingsCreateTime.start)
          let end = moment(this.userSettings.subSettingsCreateTime.end)
          this.startTime = moment().set({'hour': moment(start).hour(), 'minutes': moment(start).minute()})
          this.endTime = moment().set({'hour': moment(end).hour(), 'minutes': moment(end).minute()})
          this.standardStartTime = this.startTime
          this.standardEndTime = this.endTime
        }
      }
      setTimeout(_ => {
        document.querySelector('#create_absence_user .d-select__input-look > input[type="search"]').focus()
      }, 500)
    },
    addEventListenerSubmit () {
      if (!this.isEventListenerForSubmitAdded) {
        window.addEventListener('keydown', this.onHandleKeyboardShortcutSubmit)
        window.addEventListener('keydown', this.onHandleKeyboardShortcutEsc)
        this.isEventListenerForSubmitAdded = true
      }
    },
    removeEventListenerForSubmit () {
      window.removeEventListener('keydown', this.onHandleKeyboardShortcutSubmit)
      window.removeEventListener('keydown', this.onHandleKeyboardShortcutEsc)
      this.isEventListenerForSubmitAdded = false
    },
    closeModal () {
      this.showModal = false
      this.showErrorMessages = false
      this.isAbsenceOngoing = false
      this.reasonSearchText = ''
      this.isShowCreateAbsenceReasonButton = false
      if (this.isEndDateManipulatedFromDev) {
        this.endDate = moment(this.originalEndDate)
      }
      this.isEndDateManipulatedFromDev = false
      this.originalEndDate = null
      this.removeEventListenerForSubmit()
    },
    createAbsenceReason (titleAndDefaultPrivate) {
      const title = titleAndDefaultPrivate.title
      const defaultPrivate = titleAndDefaultPrivate.defaultPrivate
      docendo.schoolAbsenceSubjectsCreate(this.schoolId, title, '#564437', defaultPrivate, data => {
        this.$store.commit('subsAddNewAbsenceReason', data)
        this.reasonCreated = data
        this.isCreateAbsenceReason = false
      }, failure => {
        console.log('Failed', failure)
      })
    },
    setReasonSearchText (text) {
      this.reasonSearchText = text
    },
    setUsers (users) {
      this.usersAbsent = users.map(user => user.user_calendar_id)
    },
    setReason (reason) {
      this.reason = reason[0]
      // Set 'Ongoing absence' if sick and school is ids: 54378 Fløng, Nivå,
      const schoolIdsWithAutoOngoingAbsenceOnSick = [54378, 7272, 60032, 60494, 61476]
      const isActivated = schoolIdsWithAutoOngoingAbsenceOnSick.indexOf(this.schoolId) > -1
      const reasonName = reason[0] && reason[0].name ? reason[0].name.toLowerCase() : ''
      if (isActivated && reason[0] && (reasonName === 'syg' || reasonName === 'sygdom (sy)' || reasonName === 'fravær')) {
        this.setIsAbsenceOngoing(true)
      }
    },
    setStartDate (payload) {
      this.startDate = payload
    },
    setEndDate (payload) {
      this.endDate = payload
    },
    setStartTime (payload) {
      this.startTime = payload
    },
    setEndTime (payload) {
      this.endTime = payload
    },
    setStandardStartTime (payload) {
      this.standardStartTime = payload
    },
    setStandardEndTime (payload) {
      this.standardEndTime = payload
    },
    submitUser () {
      if (!this.isUserSelected || !this.isReasonSelected) {
        this.showErrorMessages = true
        return
      }
      if (!this.isOngoingAbsenceAllowed) {
        this.isAbsenceOngoing = false
      }
      let currDate = moment(this.getDate).format('YYYY-MM-DD')
      let absenceStart = moment(this.startDate).format('YYYY-MM-DD')
      let absenceEnd = moment(this.endDate).format('YYYY-MM-DD')
      let absenceOverlapsCurrentDate = moment(currDate).isBetween(absenceStart, absenceEnd, null, [])
      const startTime = this.startTime.second(0).millisecond(0).toISOString()
      const endTime = this.endTime.second(0).millisecond(0).toISOString()
      this.usersAbsent.forEach(userCalendarId => {
        docendo.schoolEventSerialsCreate(window.SCHOOL.id, userCalendarId, this.reason.id, absenceStart, absenceEnd, startTime, endTime, this.isAbsenceOngoing, this.noteForAbsentReason, data => {
          let parsedEvent = JSON.parse(data)
          // Push on page if absenceIsOnCurrentDate
          if (absenceOverlapsCurrentDate) {
            // OBS!! Takes the first event MAYBE should get from correct date
            // events: [{id: 11794027, title: "Barsel", start: "2018-09-20T11:00:00+00:00", end: "2018-09-20T13:45:00+00:00", color: "#999999", …},...]
            this.$store.commit('updateEventOnAllEventsInAHash', parsedEvent.events[0])
            this.$store.commit('addAbsenceToUserInAllUsersWithEvents', {
              cal_id: parsedEvent.calendar_id,
              event_id: parsedEvent.events[0].id
            })
          }
          this.$store.dispatch('subsGetLongAbsenceList')
        })
      })
      this.showModal = false
      this.showErrorMessages = false
      this.clearForm()
      this.$emit('created')
    },
    clearForm () {
      this.startDate = moment()
      this.endDate = moment()
      this.startTime = moment().set({'hour': 8, 'minutes': 0})
      this.endTime = moment().set({'hour': 16, 'minutes': 0})
      this.reason = ''
      this.noteForAbsentReason = ''
      this.showErrorMessages = false
      this.isAbsenceOngoing = false
    },
    submitStandardSettings () {
      let standardSubsCreateTime = {'subSettingsCreateTime': {start: this.standardStartTime, end: this.standardEndTime}}
      this.$store.dispatch('updateUserSettings', standardSubsCreateTime)
      let start = this.standardStartTime
      let end = this.standardEndTime
      this.startTime = moment().set({'hour': moment(start).hour(), 'minutes': moment(start).minute()})
      this.endTime = moment().set({'hour': moment(end).hour(), 'minutes': moment(end).minute()})
      this.isShowSettings = false
    },
    onHandleKeyboardShortcutSubmit (e) {
      // Check if the Ctrl, Alt, and K keys are pressed
      if (this.showModal && (e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault()
        this.submitUser()
      }
    },
    onHandleKeyboardShortcutEsc (e) {
      if (e.key === 'Escape') {
        e.preventDefault()
        this.closeModal()
      }
    }
  },
  mounted () {
    this.$store.dispatch('subsGetAbsenceReasons')
    this._keyListener = function (e) {
      // if (e.key === 'n' && (e.ctrlKey || e.metaKey)) {
      if (e.key === 'n') {
        if ((e.target.tagName.toUpperCase() === 'BODY')) {
          this.setShowModal()
        }
      }
    }
    document.addEventListener('keydown', this._keyListener.bind(this))
  },
  beforeDestroy () {
    this.removeEventListenerForSubmit()
    document.removeEventListener('keydown', this._keyListener)
  }
}
</script>

<style scoped>
@import '../../assets/css/variables.css';

.create-absence__form {
  padding: 1rem;
}

.error-message {
  color: #ff3860;
  font-size: 11px;
  font-weight: 400;
  padding: 1px 2px 2px;
}

.create-btn {
  width: 200px;
  height: 38px;
  margin-left: 10px;
  color: white;
  box-shadow: 0 2px 2px rgba(0, 0, 0, .1);
  background-color: var(--blue);
  border: 1px solid #4e80b7 !important;
  transition: background-color 0.3s, transform 0.3s;
  transform: scale(1);
}

.create-btn:hover {
  background-color: #66a1e4;
}

.create-btn:active {
  transform: scale(0.98)
}

.create__form-group {
  margin-bottom: 15px;
}

.absence-reason {
  display: flex;
  align-items: center;
  position: relative;
}

.create-absence-link {
  background-color: var(--blue);
  color: white;
  cursor: pointer;
}

.create__form-group label {
  letter-spacing: .075rem;
  text-transform: uppercase;
  font-size: .8rem;
  line-height: 1rem;
  margin-bottom: .5rem;
  font-weight: 700;
  display: block;
  color: #a2a3a6;
}

textarea.d-input {
  background-color: #ffffff;
}

.create__form-group__note-to-absence textarea.d-input {
  box-shadow: none;
  border: 1px solid #d8d8d8;
  background: transparent;
}

.create__form-group__note-to-absence textarea.d-input:focus, input.d-input:focus {
  border-color: var(--blue);
  background: #ffffff;
  box-shadow: 0 2px 5px -1px rgb(50 50 93 / 25%), 0 1px 3px -1px rgb(0 0 0 / 30%);
}

.absence-no-end-date {
  display: flex;
  align-items: center;
  margin-left: 1rem;
  user-select: none;
  cursor: pointer;
}

.absence-no-end-date.absence-no-end-date--active label {
  color: var(--blue);
}

.absence-no-end-date .text-explainer {
  font-size: .8rem;
  line-height: 1rem;
  font-weight: 700;
  display: block;
  color: #a2a3a6;
}

.absence-no-end-date label {
  margin-bottom: 0;
  margin-left: 0.5rem;
  margin-right: 0.5rem;
  cursor: pointer;
}

.absence-no-end-date__inner {
  display: flex;
  align-items: center;
}

.time-changer__group {
  display: flex;
  align-items: center;
}

.time-changer__settings {
  color: rgb(162, 163, 166);
}

.time-changer__settings:hover {
  color: var(--dark-blue);
}


.modal-enter {
  opacity: 0;
}

.modal-leave-active {
  opacity: 0;
}

.modal-enter .modal-container,
.modal-leave-active .modal-container {
  transform: scale(0.8);
}

.create__form-group__header {
  display: flex;
  justify-content: space-between;
}

.create__form-group__header .bttn.bttn--small {

}

.d-icon-plus {
  font-size: 13px;
}

.d-icon-plus:before {
  content: '+';
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  width: 14px;
  height: 14px;
  background-color: rgb(162, 163, 166);
  opacity: 0.6;
}

.absence-reasons__settings {
  position: absolute;
  top: 2.5px;
  right: -28px;
}

.absence-reasons__settings.icon__bg-circle:hover .d-icon-plus:before {
  opacity: 1;
  background-color: var(--blue);
}

</style>



// WEBPACK FOOTER //
// SubsCreate.vue?67528902